import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ArrowRight } from 'lucide-react';
import logo from '/logo_lightbg.svg';
import { resetPasswordSchema, type ResetPasswordData } from '@/lib/validations/auth';
import { useResetPassword, useVerifyResetToken } from '@/hooks/usePasswordReset';
import { PasswordInput } from '@/components/auth/PasswordInput';

export const Route = createFileRoute('/reset-password/$token')({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading: isVerifying, isError } = useVerifyResetToken(token);
  const { resetPassword, isLoading: isResetting } = useResetPassword(() => {
    navigate({ to: '/signin', search: { auth: 'email' } });
  });

  const form = useForm<ResetPasswordData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const onSubmit = (values: ResetPasswordData) => {
    resetPassword({ token, newPassword: values.password });
  };

  return (
    <div className="flex flex-col items-center mt-[148px]">
      <img src={logo} alt="logo" className="h-[60px] mb-8" />
      <Card className="w-[480px] h-fit">
        <CardHeader className="px-8 pt-8">
          <CardTitle className="text-gray-900">Set a new password</CardTitle>
          <CardDescription className="text-gray-700 text-base pt-2">
            Choose a new password for your Glitch account.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-8 pb-8 space-y-4">
          {isVerifying ? (
            <p className="text-sm text-gray-700">Checking link...</p>
          ) : isError || !data?.valid ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-l-4 border-l-[#B91C1C] bg-[#FEE2E2] p-4 text-sm text-gray-900">
                This reset link is invalid or has expired. Reset links are valid for 1 hour after being sent.
              </div>
              <Link
                to="/forgot-password"
                className="block text-center text-sm text-[#290BD9] hover:text-[#2309C5] underline"
              >
                Request a new reset link
              </Link>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <PasswordInput placeholder="At least 8 characters" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm New Password</FormLabel>
                      <FormControl>
                        <PasswordInput placeholder="Re-enter your password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  size="lg"
                  disabled={isResetting}
                  className="w-full py-6 bg-[#290BD9] hover:bg-[#2309C5] uppercase"
                >
                  {isResetting ? 'Updating...' : 'Update Password'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
